/**
 * 全系统共用的业务枚举与常量。
 * 前后端都从这里取，避免「后端叫 PAID、前端写成 paid」这类低级但对账致命的错误。
 */

/** 订单来源渠道：同一套后端要同时服务手机扫码和店里触屏机 */
export const OrderChannel = {
  /** 顾客用微信扫桌上二维码点餐 */
  MINI_PROGRAM: 'MINI_PROGRAM',
  /** 店里触屏机顾客自助点餐 */
  KIOSK: 'KIOSK',
  /** 店员在后台代客下单 */
  COUNTER: 'COUNTER',
} as const;
export type OrderChannel = (typeof OrderChannel)[keyof typeof OrderChannel];

/** 渠道中文名（后台、小票、报表共用一套说法，避免三个地方三种叫法） */
export const ORDER_CHANNEL_TEXT: Record<OrderChannel, string> = {
  MINI_PROGRAM: '手机扫码',
  KIOSK: '触屏自助',
  COUNTER: '后台代点',
};

/** 订单状态：待接单 → 已接单 → 制作中 → 待取餐 → 已完成 */
export const OrderStatus = {
  /** 已提交，等店员接单 */
  PENDING: 'PENDING',
  /** 店员已接单 */
  ACCEPTED: 'ACCEPTED',
  /** 厨房制作中 */
  COOKING: 'COOKING',
  /** 已出餐，等顾客取 */
  READY: 'READY',
  /** 已完成 */
  COMPLETED: 'COMPLETED',
  /** 已取消（未支付订单超时或顾客主动取消） */
  CANCELLED: 'CANCELLED',
} as const;
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];

/** 订单状态的中文名，后台和小票直接用 */
export const ORDER_STATUS_TEXT: Record<OrderStatus, string> = {
  PENDING: '待接单',
  ACCEPTED: '已接单',
  COOKING: '制作中',
  READY: '待取餐',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
};

/** 允许的状态流转。不在表里的流转一律拒绝，防止「已完成」被改回「待接单」这种脏数据。 */
export const ORDER_STATUS_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  PENDING: ['ACCEPTED', 'CANCELLED'],
  ACCEPTED: ['COOKING', 'CANCELLED'],
  COOKING: ['READY'],
  READY: ['COMPLETED'],
  COMPLETED: [],
  CANCELLED: [],
};

/** 判断一次状态流转是否合法 */
export function canTransitionOrder(from: OrderStatus, to: OrderStatus): boolean {
  if (from === to) return false;
  return (ORDER_STATUS_TRANSITIONS[from] ?? []).includes(to);
}

/**
 * 支付状态。
 * 注意：支付状态只能由服务端根据微信支付的可信结果改写，
 * 任何前端传参都不允许直接把订单变成 PAID。
 */
export const PayStatus = {
  /** 未支付 */
  UNPAID: 'UNPAID',
  /** 已发起支付，结果未知（例如微信网关超时，必须靠查单确认） */
  PAYING: 'PAYING',
  /** 已支付（以服务端验签通过的回调或查单结果为准） */
  PAID: 'PAID',
  /** 支付失败 */
  FAILED: 'FAILED',
  /** 已关闭（超时未支付） */
  CLOSED: 'CLOSED',
  /** 退款中 */
  REFUNDING: 'REFUNDING',
  /** 部分退款 */
  PARTIAL_REFUNDED: 'PARTIAL_REFUNDED',
  /** 已全额退款 */
  REFUNDED: 'REFUNDED',
} as const;
export type PayStatus = (typeof PayStatus)[keyof typeof PayStatus];

export const PAY_STATUS_TEXT: Record<PayStatus, string> = {
  UNPAID: '未支付',
  PAYING: '支付中',
  PAID: '已支付',
  FAILED: '支付失败',
  CLOSED: '已关闭',
  REFUNDING: '退款中',
  PARTIAL_REFUNDED: '部分退款',
  REFUNDED: '已退款',
};

/**
 * 打印状态：与订单状态完全独立。
 * 订单可以已经完成而小票还没打出来（打印机离线），反过来也一样。
 */
export const PrintStatus = {
  /** 待打印 */
  PENDING: 'PENDING',
  /** 打印中 */
  PRINTING: 'PRINTING',
  /** 打印成功 */
  SUCCESS: 'SUCCESS',
  /** 打印失败，可重试 */
  FAILED: 'FAILED',
  /** 已取消 */
  CANCELLED: 'CANCELLED',
} as const;
export type PrintStatus = (typeof PrintStatus)[keyof typeof PrintStatus];

export const PRINT_STATUS_TEXT: Record<PrintStatus, string> = {
  PENDING: '待打印',
  PRINTING: '打印中',
  SUCCESS: '打印成功',
  FAILED: '打印失败',
  CANCELLED: '已取消',
};

/** 打印任务类型：正常出票 / 补打 */
export const PrintJobType = {
  /** 新订单正常出票 */
  NORMAL: 'NORMAL',
  /** 补打，小票上必须明确标「补打」，避免厨房重复制作 */
  REPRINT: 'REPRINT',
} as const;
export type PrintJobType = (typeof PrintJobType)[keyof typeof PrintJobType];

/** 后台角色 */
export const UserRole = {
  /** 老板：全部权限 */
  OWNER: 'OWNER',
  /** 店长：菜单、订单、报表 */
  MANAGER: 'MANAGER',
  /** 收银/接单员：只能看和操作订单 */
  CASHIER: 'CASHIER',
  /** 厨房：只能看订单和打印 */
  KITCHEN: 'KITCHEN',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const USER_ROLE_TEXT: Record<UserRole, string> = {
  OWNER: '老板',
  MANAGER: '店长',
  CASHIER: '收银员',
  KITCHEN: '厨房',
};

/** 商品规格组类型：单选（如份量）或多选（如加料） */
export const VariantSelectType = {
  SINGLE: 'SINGLE',
  MULTIPLE: 'MULTIPLE',
} as const;
export type VariantSelectType = (typeof VariantSelectType)[keyof typeof VariantSelectType];

/** 默认币种与门店时区（中国大陆门店，报表按这个时区切天） */
export const DEFAULT_CURRENCY = 'CNY';
export const DEFAULT_TIMEZONE = 'Asia/Shanghai';

/** 未支付订单自动关闭时间（分钟） */
export const UNPAID_ORDER_TIMEOUT_MINUTES = 15;
